import { Box, Button, ButtonBase, Chip, IconButton, MenuItem, TextField, useMediaQuery } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { financeApi } from "../lib/api";
import { CategoryIcon, CategorySelectOption, categoryDisplayName, sortCategoriesHierarchically } from "../lib/format";
import type { Category, CategoryPayload, Group, ImportRowCategorySuggestion, TransactionType } from "../lib/types";
import { CloseButton } from "./CloseButton";
import { IconColorPicker } from "./IconColorPicker";
import { PanelActions, PanelHeader } from "./Panel";
import { SidePanel } from "./SidePanel";

/**
 * Nested side panel for picking the record category. Clicking a category
 * applies it immediately and closes the picker. When the search text matches
 * no existing category, an "Add category" button switches the panel to an
 * inline creation form (icon, color, parent) mirroring the Categories page.
 * The X button abandons the whole panel stack (picker and record form);
 * dismissing via backdrop/Escape closes only the picker.
 */
export function CategoryPickerPanel({
  group,
  type,
  categories,
  selectedCategoryId,
  suggestions = [],
  onApply,
  onClose,
  onCancelAll,
}: {
  group: Group;
  type: TransactionType;
  categories: Category[];
  selectedCategoryId: string;
  suggestions?: ImportRowCategorySuggestion[];
  onApply: (categoryId: string) => void;
  onClose: () => void;
  onCancelAll: () => void;
}) {
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery("(max-width: 980px)");
  const [mode, setMode] = useState<"list" | "create">("list");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [draftName, setDraftName] = useState("");
  const [nameError, setNameError] = useState("");
  const [icon, setIcon] = useState("tag");
  const [color, setColor] = useState("#2563eb");
  const [parentId, setParentId] = useState("");
  const closeRef = useRef(onClose);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const activeCategories = useMemo(
    () => categories.filter((category) => !category.is_archived && category.type === type),
    [categories, type],
  );
  const activeCategoryIds = useMemo(() => new Set(activeCategories.map((category) => category.id)), [activeCategories]);
  const searchText = search.trim().toLowerCase();
  const visibleCategories = useMemo(() => {
    if (!searchText) return sortCategoriesHierarchically(activeCategories, categories);
    const byId = new Map(activeCategories.map((category) => [category.id, category]));
    const matchedIds = new Set<string>();
    for (const category of activeCategories) {
      if (!categoryDisplayName(category, categories).toLowerCase().includes(searchText)) continue;
      matchedIds.add(category.id);
      if (category.parent_id && byId.has(category.parent_id)) matchedIds.add(category.parent_id);
    }
    return sortCategoriesHierarchically(
      activeCategories.filter((category) => matchedIds.has(category.id)),
      categories,
    );
  }, [activeCategories, categories, searchText]);
  const hasExactMatch = activeCategories.some((category) => category.name.toLowerCase() === searchText);
  const canCreate = Boolean(searchText) && !hasExactMatch;
  const visibleSuggestions = useMemo(
    () =>
      suggestions
        .filter((suggestion) => activeCategoryIds.has(suggestion.category_id))
        .sort((left, right) => right.share - left.share || right.hits - left.hits),
    [activeCategoryIds, suggestions],
  );

  const parentOptions = useMemo(
    () => sortCategoriesHierarchically(activeCategories.filter((category) => !category.parent_id), categories),
    [activeCategories, categories],
  );
  const selectedParent = parentId ? activeCategories.find((category) => category.id === parentId) : null;

  const createMutation = useMutation({
    mutationFn: (payload: CategoryPayload) => financeApi.createCategory(group.id, payload),
    onSuccess: async (category) => {
      await queryClient.invalidateQueries({ queryKey: ["categories"] });
      onApply(category.id);
      closeRef.current();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const selectCategory = (categoryId: string) => {
    onApply(categoryId);
    closeRef.current();
  };

  const openCreate = () => {
    setDraftName(search.trim());
    setNameError("");
    setStatus("");
    setMode("create");
  };

  const submitCreate = () => {
    const name = draftName.trim();
    if (!name) {
      setNameError("Name is required.");
      return;
    }
    setStatus("");
    const payload: CategoryPayload = { type, name, icon, color };
    if (parentId) payload.parent_id = parentId;
    createMutation.mutate(payload);
  };

  const renderCategoryRow = (category: Category) => {
    const selected = category.id === selectedCategoryId;
    return (
      <ButtonBase
        key={category.id}
        className={`category-picker-row${selected ? " selected" : ""}`}
        type="button"
        onClick={() => selectCategory(category.id)}
      >
        <CategorySelectOption category={category} />
        {selected ? <CheckIcon className="category-picker-check" fontSize="small" /> : null}
      </ButtonBase>
    );
  };

  return (
    <SidePanel
      ariaLabelledBy="categoryPickerTitle"
      className="category-picker-panel"
      onClose={onClose}
      onEntered={() => {
        if (isMobile || mode !== "list") return;
        searchInputRef.current?.focus();
      }}
    >
      {({ close }) => {
        closeRef.current = close;
        return mode === "create" ? (
          <>
            <Box className="records-modal-toolbar">
              <IconButton
                className="records-icon-button"
                type="button"
                aria-label="Back to category list"
                title="Back"
                onClick={() => setMode("list")}
              >
                <ArrowBackOutlinedIcon fontSize="small" />
              </IconButton>
              <h2 id="categoryPickerTitle">Add category</h2>
              <CloseButton ariaLabel="Discard and close" onClick={onCancelAll} />
            </Box>
            <Box className="category-picker-body category-picker-create-body">
              <Box className="records-field">
                <TextField
                  select
                  label="Parent category"
                  value={parentId}
                  slotProps={{
                    select: {
                      displayEmpty: true,
                      renderValue: () =>
                        selectedParent ? (
                          <span className="records-select-option">
                            <CategoryIcon icon={selectedParent.icon} color={selectedParent.color} />
                            <span>{selectedParent.name}</span>
                          </span>
                        ) : (
                          "No parent"
                        ),
                    },
                    inputLabel: { shrink: true },
                  }}
                  onChange={(event) => setParentId(String(event.target.value))}
                >
                  <MenuItem value="">No parent</MenuItem>
                  {parentOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      <span className="records-select-option">
                        <CategoryIcon icon={option.icon} color={option.color} />
                        <span>{option.name}</span>
                      </span>
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box className="category-controls-row">
                <IconColorPicker
                  color={color}
                  colorAriaLabel="Choose category color"
                  colorTitle="Category color"
                  icon={icon}
                  iconAriaLabel="Choose category icon"
                  iconInputId="categoryPickerIcon"
                  pickerId="categoryPickerIconPicker"
                  pickerLabel="Category icon"
                  onColorChange={setColor}
                  onIconChange={setIcon}
                />
                <Box className="records-field">
                  <TextField
                    required
                    autoFocus
                    fullWidth
                    error={Boolean(nameError)}
                    helperText={nameError}
                    label="Name"
                    placeholder="My category"
                    value={draftName}
                    onChange={(event) => {
                      setDraftName(event.target.value);
                      setNameError("");
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        submitCreate();
                      }
                    }}
                  />
                </Box>
              </Box>
              {status ? <p className="status error">{status}</p> : null}
            </Box>
            <PanelActions>
              <Button variant="outlined" type="button" onClick={() => setMode("list")}>
                Back
              </Button>
              <Button variant="contained" type="button" disabled={createMutation.isPending} onClick={submitCreate}>
                Create category
              </Button>
            </PanelActions>
          </>
        ) : (
          <>
            <PanelHeader titleId="categoryPickerTitle" title="Select category" closeAriaLabel="Discard and close" onClose={onCancelAll} />
            <Box className="category-picker-body">
              <TextField
                inputRef={searchInputRef}
                size="small"
                placeholder="Search or create a category"
                value={search}
                autoFocus={!isMobile}
                slotProps={{
                  htmlInput: { maxLength: 60 },
                  input: { startAdornment: <SearchIcon className="category-picker-search-icon" fontSize="small" /> },
                }}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && canCreate) {
                    event.preventDefault();
                    openCreate();
                  }
                }}
              />
              {!searchText && visibleSuggestions.length ? (
                <Box className="category-picker-suggestions">
                  <span className="category-picker-section-label">Suggested</span>
                  <Box className="category-picker-suggestion-chips">
                    {visibleSuggestions.map((suggestion) => {
                      const category = activeCategories.find((item) => item.id === suggestion.category_id);
                      const suggestionColor = category?.color || suggestion.color || undefined;
                      return (
                        <Chip
                          key={suggestion.category_id}
                          className="category-picker-suggestion-chip"
                          size="small"
                          variant="outlined"
                          clickable
                          icon={category ? <CategoryIcon icon={category.icon} color={category.color} /> : undefined}
                          label={`${category?.name || suggestion.name} · ${Math.round(suggestion.share * 100)}%`}
                          style={suggestionColor ? { borderColor: suggestionColor, color: suggestionColor } : undefined}
                          onClick={() => selectCategory(suggestion.category_id)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              ) : null}
              <Box className="category-picker-list">
                {!searchText ? (
                  <ButtonBase
                    className={`category-picker-row${!selectedCategoryId ? " selected" : ""}`}
                    type="button"
                    onClick={() => selectCategory("")}
                  >
                    <span className="category-picker-none">No category</span>
                    {!selectedCategoryId ? <CheckIcon className="category-picker-check" fontSize="small" /> : null}
                  </ButtonBase>
                ) : null}
                {visibleCategories.map(renderCategoryRow)}
                {!visibleCategories.length && !canCreate ? <p className="users-group-empty">No categories yet.</p> : null}
              </Box>
              {canCreate ? (
                <Button
                  variant="outlined"
                  size="small"
                  type="button"
                  startIcon={<AddCircleOutlineIcon fontSize="small" />}
                  onClick={openCreate}
                >
                  Add category "{search.trim()}"
                </Button>
              ) : null}
            </Box>
          </>
        );
      }}
    </SidePanel>
  );
}
